import { Box, Stack, Tab, Tabs } from "@mui/material";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { AffiliateLinkWithActions } from "Components/Affiliate/AffiliateLinkWithActions";
import { useSearchParams } from "Navigation/useSearchParams";
import { CustomerAffiliateDescription } from "Components/Affiliate/Customer/CustomerAffiliateDescription";
import { Resources, useResource } from "Translations/Resources";
import { LeadStatus } from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";
import { FilteredLeadList } from "Components/Affiliate/Stats/FilteredLeadList";
import type { AffiliateTabItem } from "Components/Affiliate/types";
import { AffiliateEarningsSummary } from "Components/Affiliate/Earnings/AffiliateEarningsSummary";
import {
  ThanksToYouNotParticipatingCard,
  isNotParticipating,
} from "Components/Affiliate/ThanksToYou/ThanksToYouNotParticipatingCard";
import { ThanksToYouParticipationSection } from "Components/Affiliate/ThanksToYou/ThanksToYouParticipationSection";
import { UserInfoGate } from "Components/Shared/UserInfoGate";
import { useThanksToYouParticipation } from "Hooks/useThanksToYouParticipation";

const RESOURCES = Resources.Components.Affiliate.UserAffiliate;

/**
 * Díky Tobě in the Customer context (ZS-1623). The page decides one thing — is the Customer in the
 * program or not — and renders either the program content (invitation link, earnings, referrals, the
 * opt-out at the bottom) or the card for a person outside it (the offer to join after a refusal, the
 * note that it is final after the confirmed opt-out). The participation comes from user-info; the gate
 * waits for it, so the children never mount with an unknown state and nothing flashes. The Hunter and
 * Merchant contexts are B2B and have no opt-out (Q1).
 */
export const CustomerAffiliatePageContent = () => {
  const { state } = useThanksToYouParticipation();

  return (
    <UserInfoGate>
      {state && isNotParticipating(state) ? (
        <ThanksToYouNotParticipatingCard state={state} />
      ) : (
        <CustomerAffiliateProgramContent />
      )}
    </UserInfoGate>
  );
};

const CustomerAffiliateProgramContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useResource();

  const tabs: Array<AffiliateTabItem> = [
    {
      id: "customer",
      label: t(RESOURCES.Tabs.Customer),
      content: (
        <Stack gap={4}>
          <AffiliateEarningsSummary />
          <PrimaryPaper>
            <CustomerAffiliateDescription />
            <AffiliateLinkWithActions
              invitedUserRole={AccessRoleCodes.Customer}
            />
          </PrimaryPaper>
          <Stack gap={1}>
            <FilteredLeadList
              leadStatus={LeadStatus.Invited}
              invitedAsRole={AccessRoleCodes.Customer}
            />
            <FilteredLeadList
              registeredAsRole={AccessRoleCodes.Customer}
              leadStatus={LeadStatus.Registered}
            />
          </Stack>
          <ThanksToYouParticipationSection />
        </Stack>
      ),
    },
  ];

  const activeTab = searchParams.get("activeTab") || tabs[0].id;

  return (
    <Box display="flex" alignItems="center" flexDirection="column">
      <Tabs
        value={activeTab}
        onChange={(_e, id: string) => setSearchParams({ activeTab: id })}
        variant="scrollable"
        sx={{
          width: "100%",
          "& .MuiTabs-scrollButtons.Mui-disabled": {
            opacity: 0.3,
          },
        }}
      >
        {tabs.map(({ label, id }) => (
          <Tab label={label} value={id} key={id} />
        ))}
      </Tabs>
      <Box mt="0.5rem" width="100%" maxWidth="600px">
        <Stack gap={4}>
          {tabs.map(({ content, id }) => (
            <div role="tabpanel" hidden={id !== activeTab} key={id}>
              {id === activeTab && content}
            </div>
          ))}
        </Stack>
      </Box>
    </Box>
  );
};
